"use client"

import { useState } from "react"
import { FormikProvider, useFormik } from "formik"
import { CheckCircle2, Files, Loader2, Sparkles } from "lucide-react"

import { DocumentFileDropzone } from "@/components/ai/knowledge-base/document-file-dropzone"
import { SUPPORTED_FILE_TYPES } from "@/components/ai/knowledge-base/types"
import { OnboardingStepActions } from "@/components/onboarding/onboarding-step-actions"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { useUploadKnowledgeBaseDocumentMutation } from "@/hooks/mutations/knowledge-base-document-upload"
import { useTranslations } from "next-intl"
import { TranslationText } from "@/components/i18n/translation-text"






type DocumentImportFormProps = {
  isDefault?: boolean
  onDocumentCreated?: (id: string) => void | Promise<void>
  maxFiles: number
  existingDocumentCount?: number
  balanceDepleted?: boolean
  showSkipAction?: boolean
  onDocumentUploaded?: () => void | Promise<void>
  onImported: () => void | Promise<void>
  onSkip: () => void
}

type DocumentImportFormValues = {
  files: File[]
}

export function DocumentImportForm({
  isDefault = true,
  onDocumentCreated,
  maxFiles,
  existingDocumentCount = 0,
  balanceDepleted = false,
  showSkipAction = true,
  onDocumentUploaded,
  onImported,
  onSkip,
}: DocumentImportFormProps) {



  const tUi = useTranslations("Onboarding.DocumentImportForm")

  const uploadDocument = useUploadKnowledgeBaseDocumentMutation()
  const [fileError, setFileError] = useState<string | null>(null)
  const [uploadedDocumentCount, setUploadedDocumentCount] = useState(
    existingDocumentCount,
  )
  const [uploadedFileNames, setUploadedFileNames] = useState<string[]>([])
  const [uploadingFileName, setUploadingFileName] = useState<string | null>(null)
  const formik = useFormik<DocumentImportFormValues>({
    initialValues: { files: [] },
    validate: ({ files }) => {
      if (files.length === 0 && uploadedDocumentCount === 0) {
        return { files: tUi("chooseAtLeastOneDocument") }
      }
      return {}
    },
    onSubmit: async (values, helpers) => {
      helpers.setStatus(undefined)
      let completedCount = uploadedDocumentCount
      let completedNames = [...uploadedFileNames]
      let remainingFiles = [...values.files]

      for (const file of values.files) {
        setUploadingFileName(file.name)
        try {
          const result = await uploadDocument.mutateAsync({
            file,
            injectionMode: "auto",
            isDefault,
          })
          await onDocumentCreated?.(result.document.id)
          completedCount += 1
          completedNames = [...completedNames, file.name]
          setUploadedDocumentCount(completedCount)
          setUploadedFileNames(completedNames)
          remainingFiles = remainingFiles.filter(
            (candidate) => getFileKey(candidate) !== getFileKey(file),
          )
          await helpers.setFieldValue(
            "files",
            remainingFiles,
            false,
          )
          await onDocumentUploaded?.()
        } catch (error) {
          helpers.setStatus(
            error instanceof Error
              ? error.message
              : `Could not upload ${file.name}.`,
          )
          setUploadingFileName(null)
          return
        }
      }

      setUploadingFileName(null)
      try {
        await onImported()
      } catch (error) {
        helpers.setStatus(
          error instanceof Error
            ? error.message
            : "Documents uploaded, but onboarding progress could not be saved.",
        )
      }
    },
  })
  const isSubmitting = formik.isSubmitting || uploadDocument.isPending
  const selectedCount = formik.values.files.length + uploadedDocumentCount
  const remainingFileSlots = Math.max(0, maxFiles - uploadedDocumentCount)

  return (
    <Card className="overflow-hidden border-blue-200 bg-gradient-to-br from-blue-50 via-white to-violet-50">
      <FormikProvider value={formik}>
        <form onSubmit={formik.handleSubmit}>
          <CardContent className="p-0">
            <div className="grid gap-6 p-4 sm:p-6 lg:grid-cols-[minmax(0,1fr)_minmax(360px,440px)] lg:items-start">
              <div className="space-y-3">
                <div className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-white px-3 py-1 text-xs font-medium text-blue-700">
                  <Sparkles className="h-3.5 w-3.5" />
                  <span key={tUi("documentSetup")}>{tUi("documentSetup")}</span></div>
                <div className="space-y-1">
                  <h2 className="text-xl font-semibold tracking-tight">
                    <span key={tUi("learnFromYourDocuments")}>{tUi("learnFromYourDocuments")}</span></h2>
                  <p className="max-w-2xl text-sm text-muted-foreground">
                    <span key={tUi("addProductGuidesFAQsPoliciesOrOtherBusiness")}>{tUi("addProductGuidesFAQsPoliciesOrOtherBusiness")}</span></p>
                  {balanceDepleted ? (
                    <p className="text-sm font-medium text-amber-700">
                      <span key={tUi("topUpYourBalanceAfterThisStepSo")}>{tUi("topUpYourBalanceAfterThisStepSo")}</span></p>
                  ) : null}
                </div>
                <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                  <span className="inline-flex items-center gap-1 rounded-full bg-white px-2.5 py-1"><TranslationText>{tUi.rich("tag0Tag0UpToValue0DocumentValue1", { value0: String(maxFiles), value1: String(maxFiles === 1 ? "" : "s"), tag0: () => (<Files className="h-3.5 w-3.5" />) })}</TranslationText></span>
                  <span className="rounded-full bg-white px-2.5 py-1">
                    <span key={tUi("pDFDOCXTXTOrMD")}>{tUi("pDFDOCXTXTOrMD")}</span></span>
                </div>
              </div>

              <div className="space-y-4 rounded-lg border bg-white p-4 shadow-sm">
                <DocumentFileDropzone
                  files={formik.values.files}
                  onFilesSelected={(files) => {
                    const nextFiles = mergeUniqueFiles(formik.values.files, files)
                    if (nextFiles.length + uploadedDocumentCount > maxFiles) {
                      setFileError(
                        `Choose no more than ${maxFiles} document${maxFiles === 1 ? "" : "s"}.`,
                      )
                      return
                    }
                    setFileError(null)
                    void formik.setFieldValue("files", nextFiles)
                  }}
                  onRemoveFile={(file) => {
                    void formik.setFieldValue(
                      "files",
                      formik.values.files.filter(
                        (candidate) => getFileKey(candidate) !== getFileKey(file),
                      ),
                    )
                  }}
                  fileError={
                    fileError ??
                    (formik.touched.files && typeof formik.errors.files === "string"
                      ? formik.errors.files
                      : null)
                  }
                  onFileError={setFileError}
                  accept={SUPPORTED_FILE_TYPES}
                  multiple
                  maxFiles={Math.max(1, remainingFileSlots)}
                  supportedTypesLabel="PDF, DOCX, TXT, MD"
                  disabled={isSubmitting || remainingFileSlots === 0}
                />

                {uploadedFileNames.length > 0 ? (
                  <div className="space-y-2" aria-live="polite">
                    {uploadedFileNames.map((fileName) => (
                      <div
                        key={fileName}
                        className="flex items-center gap-2 text-sm text-emerald-700"
                      >
                        <CheckCircle2 className="h-4 w-4" />
                        <span className="truncate"><TranslationText>{tUi.rich("value0Uploaded", { value0: String(fileName) })}</TranslationText></span>
                      </div>
                    ))}
                  </div>
                ) : null}

                {existingDocumentCount > 0 && uploadedFileNames.length === 0 ? (
                  <div className="flex items-center gap-2 text-sm text-emerald-700"><TranslationText>{tUi.rich("tag0Tag0Value0DocumentValue1AlreadyAvailable", { value0: String(existingDocumentCount), value1: String(existingDocumentCount === 1 ? tUi("is") : tUi("sAre")), tag0: () => (<CheckCircle2 className="h-4 w-4" />) })}</TranslationText></div>
                ) : null}

                {uploadingFileName ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground"><TranslationText>{tUi.rich("tag0Tag0UploadingValue0", { value0: String(uploadingFileName), tag0: () => (<Loader2 className="h-4 w-4 animate-spin" />) })}</TranslationText></div>
                ) : null}

                {formik.status ? (
                  <Alert variant="destructive">
                    <AlertDescription>{String(formik.status)}</AlertDescription>
                  </Alert>
                ) : null}
              </div>
            </div>

            <OnboardingStepActions className="border-blue-100 bg-white/80 px-6 py-4">
              {showSkipAction ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={onSkip}
                  disabled={isSubmitting}
                >
                  <span key={tUi("skipForNow")}>{tUi("skipForNow")}</span></Button>
              ) : null}
              <Button
                type="submit"
                disabled={isSubmitting || selectedCount === 0}
              >
                {isSubmitting ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : null}
                <span key={uploadedDocumentCount > 0 && formik.values.files.length === 0
                    ? tUi("continue")
                    : tUi("uploadValue0DocumentValue1", { value0: String(formik.values.files.length), value1: String(formik.values.files.length === 1 ? "" : "s") })}>
                  {uploadedDocumentCount > 0 && formik.values.files.length === 0
                    ? tUi("continue")
                    : tUi("uploadValue0DocumentValue1", { value0: String(formik.values.files.length), value1: String(formik.values.files.length === 1 ? "" : "s") })}
                </span>
              </Button>
            </OnboardingStepActions>
          </CardContent>
        </form>
      </FormikProvider>
    </Card>
  )
}

function mergeUniqueFiles(
  currentFiles: readonly File[],
  addedFiles: readonly File[],
): File[] {
  const filesByKey = new Map(
    currentFiles.map((file) => [getFileKey(file), file]),
  )
  for (const file of addedFiles) filesByKey.set(getFileKey(file), file)
  return [...filesByKey.values()]
}

function getFileKey(file: File): string {
  return `${file.name}:${file.size}:${file.lastModified}`
}
